import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiClient,
  type ShareWithUrl,
  type CreateShareRequest,
  type ShareListResponse,
} from '@/lib/api';

// Query key factory for shares
export const shareKeys = {
  all: ['shares'] as const,
  lists: () => [...shareKeys.all, 'list'] as const,
  list: () => [...shareKeys.lists()] as const,
};

/**
 * Hook for creating a share link
 * Automatically generates a shareable link for a photo
 */
export function useCreateShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateShareRequest) => {
      return apiClient.post<ShareWithUrl>('/shares', request);
    },
    onSuccess: () => {
      // Invalidate shares list to refetch with new share
      queryClient.invalidateQueries({
        queryKey: shareKeys.lists(),
      });
    },
  });
}

/**
 * Hook for fetching all shares for the current user
 */
export function useShares() {
  return useQuery({
    queryKey: shareKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<ShareListResponse>('/shares');
      return response.shares;
    },
    staleTime: 60 * 60 * 1000, // 1時間（共有リンク一覧は頻繁に変更されない）
  });
}

/**
 * Hook for deleting a share link
 */
export function useDeleteShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shareId: string) => {
      return apiClient.delete(`/shares/${shareId}`);
    },
    onSuccess: () => {
      // Invalidate shares list to refetch
      queryClient.invalidateQueries({
        queryKey: shareKeys.lists(),
      });
    },
  });
}

/**
 * Get the backend API base URL for share links
 * Share URLs are served from the backend, not the frontend
 */
function getShareBaseUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
  // Extract the origin from the API URL (remove /api/v1 suffix)
  if (apiUrl.startsWith('http')) {
    const url = new URL(apiUrl);
    return url.origin;
  }
  // Fallback to current origin for development
  return window.location.origin;
}

/**
 * Generate share URL from share token
 * Uses the backend API origin since share pages are served from the backend
 */
export function getShareUrl(token: string): string {
  const baseUrl = getShareBaseUrl();
  return `${baseUrl}/share/photo/${encodeURIComponent(token)}`;
}

/**
 * Copy share URL to clipboard
 * Prefers the shareUrl from API response if available
 */
export async function copyShareUrl(tokenOrUrl: string, isFullUrl = false): Promise<boolean> {
  const url = isFullUrl ? tokenOrUrl : getShareUrl(tokenOrUrl);
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}
